/**
 * Mapping helpers per convertire SagraListing in EventCardProps
 */

import type { SagraListing } from "@/types/sagra";
import type { EventCardProps } from "@/components/cards/EventCard";
import { displayComune, siglaProvincia } from "./geo-resolver";
import { formatDateRange, monthNameIT } from "./dates";
import { buildSlugEid } from "./route";
import { toSagraHref, toSagraEditionHref } from "./routes";

// Note: Le funzioni di calcolo degli stati sono state spostate in scripts/build-date-indexes.ts
// per essere calcolate al build time invece che al runtime (SEO-friendly)

/**
 * Converte SagraListing in EventCardProps
 */
export function sagraToEventCard(
  sagra: SagraListing,
  options: {
    showProvince?: boolean;
    showMotherLink?: boolean;
  } = {}
): EventCardProps {
  const { showProvince = true, showMotherLink = true } = options;

  // Determina se è TBA
  const isTBA = !sagra.startDate && (sagra.tba || !!sagra.monthHint);

  // Calcola dateLabel
  let dateLabel: string;
  if (sagra.startDate) {
    // Ha date: mostra range formattato
    dateLabel = formatDateRange(sagra.startDate, sagra.endDate);
    // Aggiungi anno se non è l'anno corrente
    const year = new Date(sagra.startDate).getFullYear();
    const currentYear = new Date().getFullYear();
    if (year !== currentYear) {
      dateLabel += ` ${year}`;
    }
  } else if (isTBA && sagra.monthHint && sagra.currentYear) {
    // TBA: mostra mese e anno
    dateLabel = `${monthNameIT(sagra.monthHint)} ${sagra.currentYear} · Date in arrivo`;
  } else {
    dateLabel = "Date da confermare";
  }

  // Locality con sigla provincia
  const comuneDisplay = displayComune(sagra.comune);
  const provinciaDisplay = siglaProvincia(sagra.provincia);
  const locality = showProvince ? `${comuneDisplay} (${provinciaDisplay})` : comuneDisplay;

  // URL alla pagina madre
  const slugEid = buildSlugEid(sagra.slug, sagra.id);
  const motherUrl = toSagraHref(slugEid);
  // Costruisci targetUrl se manca (per oggetti dal nearby-index.json)
  const year = (sagra as any).year ?? sagra.currentYear;
  const targetUrl =
    sagra.targetUrl || (year ? toSagraEditionHref(slugEid, year) : motherUrl);
  const isTargetEdition = targetUrl !== motherUrl && !!year;

  return {
    title: sagra.title,
    href: targetUrl,
    dateLabel,
    isTBA,
    // Usa stati pre-calcolati al build time (SEO-friendly)
    isToday: sagra.isToday,
    isTomorrow: sagra.isTomorrow,
    isThisWeekend: sagra.isThisWeekend,
    isThisWeek: sagra.isThisWeek,
    isComingSoon: sagra.isComingSoon,
    isConcluded: sagra.isConcluded,
    isFree: false, // TODO: aggiungere quando disponibile nei dati
    locality,
    tags: [], // TODO: aggiungere quando disponibili nei dati
    motherHref: showMotherLink && isTargetEdition ? motherUrl : undefined,
  };
}
